import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useApiQuery } from '@/hooks/useApiQuery';

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
}

interface DocumentType {
  id: number;
  name: string;
  description: string | null;
}

interface UploadDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  kycRequestId?: number;
}

interface QueuedFile {
  id: string;
  file: File;
  docType: string;
  customerId: string;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error: string;
}

export default function UploadDocumentModal({ isOpen, onClose, onSuccess, kycRequestId }: UploadDocumentModalProps) {
  const { accessToken } = useAuth();
  const { data: docTypes, loading: typesLoading } = useApiQuery<DocumentType[]>({ endpoint: '/document-types' });
  const { data: customersData, loading: customersLoading } = useApiQuery<Customer[]>({ endpoint: '/customers' });

  const [customerId, setCustomerId] = useState('');
  const [docType, setDocType] = useState('');
  const [files, setFiles] = useState<QueuedFile[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const customers = customersData || [];

  useEffect(() => {
    if (isOpen) {
      setCustomerId('');
      setDocType((docTypes && docTypes[0]?.name) || '');
      setFiles([]);
      setError('');
      setSubmitting(false);
    }
  }, [isOpen, docTypes]);

  if (!isOpen) return null;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected || selected.length === 0) return;
    const newFiles: QueuedFile[] = Array.from(selected).map((f) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file: f,
      docType: docType || (docTypes && docTypes[0]?.name) || '',
      customerId: customerId || '',
      status: 'pending' as const,
      error: '',
    }));
    setFiles((prev) => [...prev, ...newFiles]);
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const updateFile = (id: string, updates: Partial<QueuedFile>) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...updates } : f)));
  };

  const uploadSingle = async (qf: QueuedFile) => {
    if (!qf.file || !qf.docType) return false;

    const formData = new FormData();
    formData.append('file', qf.file);
    formData.append('docType', qf.docType);
    formData.append('customerId', qf.customerId || '');
    if (kycRequestId) formData.append('kycRequestId', String(kycRequestId));
    formData.append('docCategory', mapCategory(qf.docType));

    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL || 'https://ekyc.tumkurconnect.com/api/v1'}/documents`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });
      const json = await res.json();
      if (!res.ok) {
        updateFile(qf.id, { status: 'error', error: json.message || 'Upload failed' });
        return false;
      }
      updateFile(qf.id, { status: 'done', error: '' });
      return true;
    } catch {
      updateFile(qf.id, { status: 'error', error: 'Network error' });
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (files.length === 0) { setError('Please add at least one file'); return; }
    if (files.some((f) => !f.docType)) { setError('All files need a document type'); return; }
    if (files.some((f) => !f.customerId)) { setError('All files need a customer selected'); return; }

    setSubmitting(true);
    setError('');

    let successCount = 0;
    for (const qf of files) {
      if (qf.status !== 'done') {
        updateFile(qf.id, { status: 'uploading' });
        const ok = await uploadSingle(qf);
        if (ok) successCount++;
      }
    }

    setSubmitting(false);
    if (successCount === files.length) {
      onSuccess();
      onClose();
    } else if (successCount > 0) {
      setError(`${successCount} of ${files.length} uploaded. Fix errors and retry.`);
      onSuccess();
    }
  };

  const mapCategory = (type: string) => {
    const map: Record<string, string> = {
      Aadhaar: 'identity',
      PAN: 'identity',
      'Address Proof': 'address',
      Photo: 'photo',
      Signature: 'signature',
    };
    return map[type] || 'identity';
  };

  const totalDone = files.filter((f) => f.status === 'done').length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <h4 className="text-lg font-bold text-stone-900 mb-1">Upload Documents</h4>
        <p className="text-stone-400 text-xs mb-4">Select customers, types, and add multiple files for batch upload.</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Default Document Type */}
          <div>
            <label className="block text-stone-700 text-sm font-medium mb-1.5">Default Document Type</label>
            <select
              value={docType}
              onChange={(e) => { setDocType(e.target.value); setFiles((prev) => prev.map((f) => ({ ...f, docType: e.target.value }))); }}
              disabled={typesLoading}
              className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white disabled:bg-stone-50"
            >
              {typesLoading && <option value="">Loading...</option>}
              {docTypes?.map((t) => (
                <option key={t.id} value={t.name}>{t.name}</option>
              ))}
            </select>
            <p className="text-stone-400 text-[10px] mt-1">You can override per-file below.</p>
          </div>

          {/* Default Customer */}
          <div>
            <label className="block text-stone-700 text-sm font-medium mb-1.5">Default Customer</label>
            <select
              value={customerId}
              onChange={(e) => { setCustomerId(e.target.value); setFiles((prev) => prev.map((f) => ({ ...f, customerId: e.target.value }))); }}
              disabled={customersLoading}
              className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white disabled:bg-stone-50"
            >
              {customersLoading && <option value="">Loading customers...</option>}
              {!customersLoading && customers.length === 0 && (
                <option value="">No customers found</option>
              )}
              {!customersLoading && customers.length > 0 && (
                <>
                  <option value="">Select a customer...</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.first_name} {c.last_name} ({c.email})
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>

          {/* File Drop / Select */}
          <div>
            <label className="block text-stone-700 text-sm font-medium mb-1.5">Files</label>
            <div className="border-2 border-dashed border-stone-200 rounded-xl p-6 text-center hover:border-brand-400 transition-colors">
              <input
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                id="doc-upload-batch"
                accept="image/jpeg,image/png,image/jpg,application/pdf"
              />
              <label htmlFor="doc-upload-batch" className="cursor-pointer flex flex-col items-center gap-2">
                <i className="ri-upload-cloud-line text-stone-300 text-3xl" />
                <span className="text-stone-500 text-xs">Click or drag files here</span>
                <span className="text-stone-400 text-[10px]">JPG, PNG, or PDF up to 10MB each</span>
              </label>
            </div>
          </div>

          {/* File Queue */}
          {files.length > 0 && (
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              <div className="flex items-center justify-between text-xs text-stone-500 mb-1">
                <span>{files.length} file{files.length > 1 ? 's' : ''}</span>
                <span>{totalDone} done</span>
              </div>
              {files.map((qf) => (
                <div key={qf.id} className="border border-stone-100 rounded-lg p-3 bg-stone-50/50">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-md bg-stone-100 flex items-center justify-center shrink-0">
                      <i className="ri-file-line text-stone-400 text-sm w-4 h-4 flex items-center justify-center" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-stone-900 text-xs font-medium truncate">{qf.file.name}</p>
                      <p className="text-stone-400 text-[10px]">{(qf.file.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(qf.id)}
                      className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors"
                      title="Remove"
                    >
                      <i className="ri-close-line text-sm w-4 h-4 flex items-center justify-center" />
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={qf.docType}
                      onChange={(e) => updateFile(qf.id, { docType: e.target.value })}
                      className="flex-1 min-w-0 border border-stone-200 rounded-md px-2 py-1.5 text-xs text-stone-900 bg-white"
                    >
                      {docTypes?.map((t) => (
                        <option key={t.id} value={t.name}>{t.name}</option>
                      ))}
                    </select>
                    <select
                      value={qf.customerId}
                      onChange={(e) => updateFile(qf.id, { customerId: e.target.value })}
                      className="flex-1 min-w-0 border border-stone-200 rounded-md px-2 py-1.5 text-xs text-stone-900 bg-white"
                    >
                      <option value="">No customer</option>
                      {customers.map((c) => (
                        <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
                      ))}
                    </select>
                  </div>
                  {qf.status === 'uploading' && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-brand-600">
                      <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center" />
                      Uploading...
                    </div>
                  )}
                  {qf.status === 'done' && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-emerald-600">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center" />
                      Uploaded
                    </div>
                  )}
                  {qf.status === 'error' && (
                    <div className="mt-2 text-xs text-red-600">
                      {qf.error}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-100 rounded-lg px-3 py-2 text-red-600 text-xs">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-full border border-stone-200 text-stone-600 font-medium text-sm hover:bg-stone-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || files.length === 0}
              className="flex-1 bg-brand-600 hover:bg-brand-700 disabled:bg-brand-300 text-white font-semibold py-2.5 rounded-full text-sm transition-colors flex items-center justify-center gap-2"
            >
              {submitting && <i className="ri-loader-4-line animate-spin" />}
              {submitting ? 'Uploading...' : `Upload ${files.length > 0 ? `(${files.length})` : ''}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
